import { Component } from "react"

/* Redux */
import { connect } from "react-redux"

/* Components */
import { InnerLayout } from "../../components"

/* Widgets */
import { Tabs, RsgGameForm } from "../../widgets/Rsg"

/* REST API */
import { games } from "../../api/Rsg"

/* Helpers */
import { withRouter, utils } from "../../helpers"

/* Icons */
import { SearchIcon, CloseIcon, EditIcon, LockIcon, TableIcon, StudioIcon, KeyIcon, BanIcon, CheckIcon } from "../../widgets/Rsg/RsgIcons"

/* Translate */
import { t } from "../../translates/translate"


/* STATES OF A TABLE INSIDE THE REPORTS */
const STATES = ["", "active", "off", "empty"]


/* Page RSG Games */
class RsgGames extends Component {

    constructor() {
        super()

        this.state = {
            data: [],
            status: "loading",
            search: "",
            state: "",
            canEdit: false,
        }
    }

    componentDidMount = () => {
        window.scrollTo(0, 0)
        this.load()
    }

    /* Load Action */
    load = (reload = false) => {

        /* Check Reload */
        if (reload) {
            this.setState({ status: "loading" })
        }

        /* Send to REST API */
        games().then((response) => {
            if (response.status === 200) {
                this.setState({ status: "ok", data: response.data.list, canEdit: utils.checkBoolean(response.data.canEdit) })
            }
            else if (response.status === 401 || response.status === 403) {
                this.setState({ status: "permission", data: [] })
            }
            else if (response.status === 498) {
                this.setState({ status: "key", data: [] })
            }
            else {
                this.setState({ status: "error", data: [] })
            }
        }).catch(() => {
            this.setState({ status: "network", data: [] })
        })

    }

    /* State Of A Row — a table without a game ID never reaches the report */
    getState = (item) => {

        /* Check Mapping */
        if (utils.checkValue(item.rsGameID, "") === "") {
            return "empty"
        }

        /* Send Result */
        return parseInt(item.isActive) === 1 ? "active" : "off"
    }

    /* Rows Of The Filters — the whole mapping arrives at once, so the search stays here */
    getRows = () => {

        /* Fields */
        const { data, search, state } = this.state
        const value = search.trim().toLowerCase()

        /* Send Result */
        return data.filter((item) => {

            /* Check State */
            if (state !== "" && this.getState(item) !== state) {
                return false
            }

            /* Check Search */
            if (value === "") {
                return true
            }

            const name = String(utils.checkValue(item.tableName, "")).toLowerCase()
            const slug = String(utils.checkValue(item.tableSlug, "")).toLowerCase()
            const studio = String(utils.checkValue(item.studioName, "")).toLowerCase()
            const gameID = String(utils.checkValue(item.rsGameID, "")).toLowerCase()

            return name.includes(value) || slug.includes(value) || studio.includes(value) || gameID.includes(value)
        })
    }

    /* Draw Search */
    _search = () => {

        /* Fields */
        const { search } = this.state

        return (
            <div className="rsg--search">

                <SearchIcon />

                <input
                    value={search}
                    onChange={(event) => this.setState({ search: event.target.value })}
                    placeholder={t("rsg.searchByTable")}
                />

                {search !== "" &&
                    <div onClick={() => this.setState({ search: "" })} className="rsg--search-clear">
                        <CloseIcon size={12} />
                    </div>
                }

            </div>
        )
    }

    /* Draw State Filter */
    _filter = () => {

        /* Fields */
        const { state } = this.state

        return (
            <div className="rsg--filter">
                {STATES.map((item, index) =>
                    <div
                        onClick={() => item !== state && this.setState({ state: item })}
                        className={`rsg--filter-item ${item === state ? "active" : ""}`}
                        key={`${index}`}
                    >
                        {item === "" ? t("rsg.allTables") : t(`rsg.state.${item}`)}
                    </div>
                )}
            </div>
        )
    }

    /* Draw Summary — how many tables the regulator really receives */
    _summary = () => {

        /* Fields */
        const { data } = this.state
        const active = data.filter((item) => this.getState(item) === "active").length
        const off = data.filter((item) => this.getState(item) === "off").length
        const empty = data.filter((item) => this.getState(item) === "empty").length

        return (
            <div className="rsg--summary">

                <div className="rsg--summary-item">
                    <div className="rsg--summary-title">{t("rsg.state.active")}</div>
                    <div className="rsg--summary-value green">{active}</div>
                </div>

                <div className="rsg--summary-item">
                    <div className="rsg--summary-title">{t("rsg.state.off")}</div>
                    <div className="rsg--summary-value">{off}</div>
                </div>

                <div className="rsg--summary-item">
                    <div className="rsg--summary-title">{t("rsg.state.empty")}</div>
                    <div className={`rsg--summary-value ${empty > 0 ? "red" : ""}`}>{empty}</div>
                </div>

                <div className="rsg--summary-item">
                    <div className="rsg--summary-title">{t("rsg.allTables")}</div>
                    <div className="rsg--summary-value">{data.length}</div>
                </div>

            </div>
        )
    }

    /* Draw State Of A Row */
    _state = (item) => {

        /* Fields */
        const state = this.getState(item)

        /* Check State */
        if (state === "empty") {
            return (
                <div className="rsg--status red">
                    <BanIcon size={12} />
                    <span>{t("rsg.noGameID")}</span>
                </div>
            )
        }

        /* Check Status */
        if (state === "off") {
            return (
                <div className="rsg--status grey">
                    <BanIcon size={12} />
                    <span>{t("rsg.notInReport")}</span>
                </div>
            )
        }

        return (
            <div className="rsg--status green">
                <CheckIcon size={11} />
                <span>{t("rsg.inReport")}</span>
            </div>
        )
    }

    /* Draw Content */
    _content = () => {

        /* Fields */
        const { status, data, search, canEdit } = this.state
        const rows = this.getRows()

        /* Check Status */
        if (status !== "ok") {
            return (
                <div className="rsg--page">
                    <InnerLayout inner status={status} reload={() => this.load(true)} />
                </div>
            )
        }

        return (
            <div className="rsg--page">

                {/* Draw Summary */}
                {data.length > 0 && this._summary()}

                {/* Head */}
                <div className="rsg--table-header">
                    <div className="rsg--column number">#</div>
                    <div className="rsg--column table">{t("rsg.tableName")}</div>
                    <div className="rsg--column studio">{t("rsg.studio")}</div>
                    <div className="rsg--column gameid">{t("rsg.gameID")}</div>
                    <div className="rsg--column state">{t("rsg.status")}</div>
                    <div className="rsg--column actions" />
                </div>

                {/* Empty */}
                {rows.length === 0 &&
                    <div className="rsg--empty">
                        <TableIcon size={40} />
                        <span>{search !== "" || data.length > 0 ? t("rsg.nothingFound") : t("rsg.gamesEmpty")}</span>
                    </div>
                }

                {/* Body */}
                {rows.map((item, index) =>
                    <div className="rsg--row" key={`${index}`}>

                        <div className="rsg--column number">{index + 1}</div>

                        <div className="rsg--column table">

                            <div className="rsg--file-icon light"><TableIcon size={16} /></div>

                            <div className="rsg--file-text">
                                <div className="rsg--file-name">{utils.checkValue(item.tableName, "-")}</div>
                                <div className="rsg--file-type lower">{utils.checkValue(item.tableSlug, "-")}</div>
                            </div>

                        </div>

                        <div className="rsg--column studio">
                            <div className="rsg--studio">
                                <StudioIcon size={12} />
                                <span>{utils.checkValue(item.studioName, "-")}</span>
                            </div>
                        </div>

                        <div className="rsg--column gameid">
                            {utils.checkValue(item.rsGameID, "") !== ""
                                ? <div className="rsg--gameid">
                                    <KeyIcon size={12} />
                                    <span>{item.rsGameID}</span>
                                </div>
                                : <div className="rsg--gameid-empty">{t("rsg.gameIDPlaceholder")}</div>
                            }
                        </div>

                        <div className="rsg--column state">{this._state(item)}</div>

                        <div className="rsg--column actions">
                            {canEdit
                                ? <div onClick={() => this._form.open({ item })} className="rsg--action" title={t("rsg.gameTitle")}>
                                    <EditIcon />
                                </div>
                                : <div className="rsg--action-locked">
                                    <LockIcon />
                                    <span>{t("rsg.superadminOnly")}</span>
                                </div>
                            }
                        </div>

                    </div>
                )}

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { status } = this.state

        return (
            <div className="page">

                <div className="page-header rsg--header">

                    <h1>{t("rsg.gamesTitle")}</h1>

                    {status === "ok" &&
                        <div className="rsg--tools">

                            {/* Draw Search */}
                            {this._search()}

                            {/* Draw State Filter */}
                            {this._filter()}

                        </div>
                    }

                </div>

                {/* Tabs */}
                <Tabs active="games" />

                {/* Draw Content */}
                {this._content()}

                {/* Draw Game Modal */}
                <RsgGameForm ref={(ref) => this._form = ref} reload={() => this.load()} />

            </div>
        )
    }

}

const mapStateToProps = (state) => ({
    admin: state.admin,
    language: state.language,
})

export default connect(mapStateToProps)(withRouter(RsgGames))
